import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomSelect from "../CustomSelect/CustomSelect";
import styles from "./DateField.module.scss";

const MONTHS = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

const pad = (n: number) => String(n).padStart(2, "0");
const toISO = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

const parseISO = (s?: string) => {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return { y: +m[1], m: +m[2] - 1, d: +m[3] };
};

const daysInMonth = (y: number, m: number) => new Date(y, m + 1, 0).getDate();
// Понедельник — первый день недели.
const firstWeekday = (y: number, m: number) => (new Date(y, m, 1).getDay() + 6) % 7;

interface Props {
  id?: string;
  value: string;
  onChange: (iso: string) => void;
  onBlur?: () => void;
  min: string;
  max: string;
  placeholder?: string;
}

const DateField: React.FC<Props> = ({
  id,
  value,
  onChange,
  onBlur,
  min,
  max,
  placeholder = "дд.мм.гггг",
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = parseISO(value);
  const minD = parseISO(min);
  const maxD = parseISO(max);

  const fallbackYear = maxD?.y ?? new Date().getFullYear();
  const [viewY, setViewY] = useState(selected?.y ?? fallbackYear);
  const [viewM, setViewM] = useState(selected?.m ?? 0);

  // Открыли повторно — показываем месяц уже выбранной даты.
  useEffect(() => {
    if (!open) return;
    setViewY(selected?.y ?? fallbackYear);
    setViewM(selected?.m ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onBlur]);

  const years = useMemo(() => {
    const from = minD?.y ?? fallbackYear - 100;
    const to = maxD?.y ?? fallbackYear;
    const list: { value: string; label: string }[] = [];
    for (let y = to; y >= from; y--) list.push({ value: String(y), label: String(y) });
    return list;
  }, [minD?.y, maxD?.y, fallbackYear]);

  const monthOptions = useMemo(
    () => MONTHS.map((label, i) => ({ value: String(i), label })),
    []
  );

  const cells = useMemo(() => {
    const total = daysInMonth(viewY, viewM);
    const lead = firstWeekday(viewY, viewM);
    const out: (number | null)[] = Array(lead).fill(null);
    for (let d = 1; d <= total; d++) out.push(d);
    return out;
  }, [viewY, viewM]);

  const isDisabled = (d: number) => {
    const iso = toISO(viewY, viewM, d);
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return false;
  };

  const pick = (d: number) => {
    if (isDisabled(d)) return;
    onChange(toISO(viewY, viewM, d));
    setOpen(false);
    onBlur?.();
  };

  const display = selected
    ? `${pad(selected.d)}.${pad(selected.m + 1)}.${selected.y}`
    : placeholder;

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`${styles.trigger}${open ? " " + styles.open : ""}${
          selected ? "" : " " + styles.isPlaceholder
        }`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.value}>{display}</span>
        <span className={styles.calendarIcon} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.popover} role="dialog" aria-label="Выбор даты">
          <div className={styles.header}>
            <CustomSelect
              options={monthOptions}
              value={String(viewM)}
              onChange={(v) => setViewM(Number(v))}
            />
            <CustomSelect
              options={years}
              value={String(viewY)}
              onChange={(v) => setViewY(Number(v))}
            />
          </div>

          <div className={styles.weekdays}>
            {WEEKDAYS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>

          <div className={styles.grid}>
            {cells.map((d, i) =>
              d === null ? (
                <span key={`e${i}`} />
              ) : (
                <button
                  key={d}
                  type="button"
                  className={`${styles.day}${
                    selected &&
                    selected.y === viewY &&
                    selected.m === viewM &&
                    selected.d === d
                      ? " " + styles.selected
                      : ""
                  }`}
                  disabled={isDisabled(d)}
                  onClick={() => pick(d)}
                >
                  {d}
                </button>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateField;
